// src/admin/AdminPendingVideos.js
import React, { useEffect, useState } from 'react';
import {
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Switch,
  TextField,
  Box,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../utils/api';
import { API } from '../config/apiConfig';
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';

function AdminPendingVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [openPlayer, setOpenPlayer] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [currentSearch, setCurrentSearch] = useState('');

  // Load 100 pending videos on mount and when search is performed
  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      setErrorMsg('');
      
      try {
        const params = { limit: 100 };
        if (currentSearch && currentSearch.trim() !== '') {
          params.searchTerm = currentSearch.trim();
        }

        const res = await api.get(`${API.adminVideos}/pending`, { params });
        const newVideos = res.data?.videos || [];
        setVideos(newVideos);
      } catch (err) {
        console.error('❌ Failed to fetch pending videos:', err);
        setErrorMsg(err.response?.data?.error || 'Failed to fetch pending videos');
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [currentSearch]);

  const handleSearch = () => {
    setCurrentSearch(searchTerm);
  };

  const handlePlay = (video) => {
    console.log('🎬 Playing video:', video);
    setCurrentVideo(video);
    setOpenPlayer(true);
  };

  const handlePlayerClose = () => {
    setOpenPlayer(false);
    setCurrentVideo(null);
  };

  const handleToggleStatus = async (videoId, newStatus) => {
    const prev = [...videos];
    setVideos((list) =>
      list.map((v) => (v.VideoID === videoId ? { ...v, IsActive: newStatus } : v))
    );

    try {
      if (newStatus) {
        await api.post(`${API.adminVideos}/${videoId}/approve`);
        setSuccessMsg('✅ Video approved successfully');
        // Remove approved video from pending list
        setVideos(prev => prev.filter(v => v.VideoID !== videoId));
      } else {
        await api.post(`${API.adminVideos}/${videoId}/reject`);
        setSuccessMsg('❌ Video rejected successfully');
        // Remove rejected video from pending list
        setVideos(prev => prev.filter(v => v.VideoID !== videoId));
      }
    } catch (err) {
      console.error('❌ Failed to update video status:', err.response?.data || err.message);
      setVideos(prev); // rollback
      setErrorMsg(err.response?.data?.error || 'Failed to update video status');
    }
  };

  const handleDeleteClick = (video) => {
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`${API.adminVideos}/${videoToDelete.VideoID}`);
      
      // Remove deleted video from list
      setVideos(prev => prev.filter(v => v.VideoID !== videoToDelete.VideoID));
      setSuccessMsg('Video deleted successfully');
      setDeleteDialogOpen(false);
      setVideoToDelete(null);
    } catch (err) {
      console.error('❌ Error deleting video:', err);
      setErrorMsg(err.response?.data?.error || 'Failed to delete video');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminPageWrapper 
      title="Pending Videos" 
      subtitle="Review and approve videos waiting for approval"
    >

      {/* ✅ Search input */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search by title, category, uploader"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          sx={(theme) => ({
            '& .MuiOutlinedInput-root': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(102, 126, 234, 0.5)',
              },
              '&.Mui-focused': {
                borderColor: '#667eea',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
              },
            },
          })}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
            },
            '&:disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
            transition: 'all 0.3s ease',
            px: 3,
          }}
        >
          Search
        </Button>
      </Stack>

      <ProfessionalTable
        columns={[
          {
            header: 'Thumbnail',
            accessor: 'ThumbnailUrl',
            render: (thumbnailUrl) => (
              thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt="thumbnail"
                  style={{ width: 100, height: 60, borderRadius: 4, objectFit: 'cover' }}
                  onError={(e) => {
                    console.error('❌ Thumbnail load error:', {
                      url: thumbnailUrl,
                      error: e
                    });
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.nextSibling) {
                      e.currentTarget.nextSibling.style.display = 'block';
                    }
                  }}
                  onLoad={() => {}}
                />
              ) : (
                <div style={{
                  width: 100,
                  height: 60,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  No thumbnail
                </div>
              )
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Title',
            accessor: 'Title',
            render: (title) => (
              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {title}
              </Typography>
            )
          },
          {
            header: 'Category',
            accessor: 'CategoryName',
            render: (category) => (
              <Chip label={category} size="small" color="primary" variant="outlined" />
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Uploader',
            accessor: 'UploaderName',
            render: (uploader) => (
              <Typography variant="body2">{uploader}</Typography>
            )
          },
          {
            header: 'Status',
            accessor: 'Status',
            render: (status) => (
              <Chip 
                label={status}
                color="warning"
                size="small"
              />
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Preview',
            accessor: 'VideoUrl',
            render: (videoUrl, row) => (
              videoUrl ? (
                <IconButton onClick={() => handlePlay(row)} size="small" title="Play">
                  <PlayCircleIcon />
                </IconButton>
              ) : (
                <Typography variant="body2" color="text.secondary">-</Typography>
              )
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Approve',
            accessor: 'IsActive',
            render: (isActive, row) => (
              <Switch
                checked={false}
                onChange={(e) => handleToggleStatus(row.VideoID, e.target.checked)}
                color="primary"
              />
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Actions',
            accessor: 'VideoID',
            render: (videoId, row) => (
              <IconButton
                color="error"
                size="small"
                onClick={() => handleDeleteClick(row)}
                title="Delete Video"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            ),
            style: { textAlign: 'center' }
          }
        ]}
        data={videos.map(video => ({ ...video, id: video.VideoID }))}
        loading={loading}
        emptyMessage={videos.length === 0 && !loading ? (currentSearch ? "No pending videos found" : "No pending videos available") : ""}
      />

      {/* Video preview dialog */}
      <Dialog open={openPlayer} onClose={handlePlayerClose} maxWidth="md" fullWidth>
        <DialogContent>
          {currentVideo?.VideoUrl ? (
            <video
              key={currentVideo.VideoID}
              width="100%"
              height="auto"
              controls
              src={currentVideo.VideoUrl}
              style={{ borderRadius: 8 }}
              onError={(e) => {
                console.error('❌ Video load error:', currentVideo.VideoUrl);
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextSibling.style.display = 'block';
              }}
              onLoad={() => console.log('✅ Video loaded:', currentVideo.VideoUrl)}
            />
          ) : null}
          <div 
            style={{ 
              display: currentVideo?.VideoUrl ? 'none' : 'block',
              textAlign: 'center',
              padding: '2rem',
              backgroundColor: '#f5f5f5',
              borderRadius: 8
            }}
          >
            <Typography variant="h6" color="textSecondary">
              {currentVideo?.VideoUrl ? 'Failed to load video' : 'No video URL available'}
            </Typography>
            {currentVideo?.VideoUrl && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                URL: {currentVideo.VideoUrl}
              </Typography>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Video</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{videoToDelete?.Title}"?
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!errorMsg || !!successMsg}
        autoHideDuration={4000}
        onClose={() => {
          setErrorMsg('');
          setSuccessMsg('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={errorMsg ? 'error' : 'success'}
          onClose={() => {
            setErrorMsg('');
            setSuccessMsg('');
          }}
          sx={{ width: '100%' }}
        >
          {errorMsg || successMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}

export default AdminPendingVideos;

